import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { AgeCategory } from "@shared/graphql";
import { classNames } from "@ct-react/core";
import Button from "../common/button";
import "./guests.scss";

const transDefs = defineMessages({
  adults: { id: "components.inputs.guests.adult", defaultMessage: "Adultes" },
  children: { id: "components.inputs.guests.children", defaultMessage: "Enfants" },
  exempt: { id: "components.inputs.guests.exempt", defaultMessage: "Exemptés" },
  helper: { id: "components.inputs.guests.children-helper", defaultMessage: "de {from} à {to} ans" },
});

type Props = {
  className?: string;
  max?: number,
  value?: Record<AgeCategory, number>;
  onChange?: (value?: Record<AgeCategory, number>) => void;
}

const GuestsSelector = (
  {
    className,
    max = 10,
    value,
    onChange = () => void 0
  }: Props) => {

  const intl = useIntl();

  const counted = useCallback((guests?: Record<AgeCategory, number>) =>
    Object.values(guests || {}).reduce((acc, cur) => acc + cur, 0), []);

  const onValueChange = (type: AgeCategory, increment: 1 | -1) => {
    const newValue = {
      ...value,
      [type]: !!value && value[type] ? value[type] + increment : increment,
    } as Record<AgeCategory, number>;
    onChange(counted(newValue) === 0 ? undefined : newValue);
  }

  const wrapperClasses = classNames("guests-editor", className);

  return (
    <dl className={wrapperClasses}>
      <dt>{intl.formatMessage(transDefs.adults)}</dt>
      <dd>
        <Button type="button"
                className="primary outline with-icon"
                disabled={!value || !value[AgeCategory.ADULT] || value[AgeCategory.ADULT] === 0}
                onClick={() => onValueChange(AgeCategory.ADULT, -1)}>
          <IconMinus className="m-icon" />
        </Button>
        <span>{value?.ADULT || 0}</span>
        <Button type="button"
                className="primary outline with-icon"
                disabled={counted(value) === max}
                onClick={() => onValueChange(AgeCategory.ADULT, 1)}>
          <IconPlus className="p-icon" />
        </Button>
      </dd>
      <dt>
        {intl.formatMessage(transDefs.children)}
        <span className="helper">{intl.formatMessage(transDefs.helper, { from: 6, to: 16 })}</span>
      </dt>
      <dd>
        <Button type="button"
                className=" primary outline with-icon"
                disabled={!value || !value[AgeCategory.CHILD] || value[AgeCategory.CHILD] === 0}
                onClick={() => onValueChange(AgeCategory.CHILD, -1)}>
          <IconMinus className="m-icon" />
        </Button>
        <span>{value?.CHILD || 0}</span>
        <Button type="button"
                className="primary outline with-icon"
                disabled={counted(value) === max}
                onClick={() => onValueChange(AgeCategory.CHILD, 1)}>
          <IconPlus className="p-icon" />
        </Button>
      </dd>
      <dt>
        {intl.formatMessage(transDefs.exempt)}
        <span className="helper">{intl.formatMessage(transDefs.helper, { from: 0, to: 5 })}</span>
      </dt>
      <dd>
        <Button type="button"
                className="primary outline with-icon"
                disabled={!value || !value[AgeCategory.EXEMPT] || value[AgeCategory.EXEMPT] === 0}
                onClick={() => onValueChange(AgeCategory.EXEMPT, -1)}>
          <IconMinus className="m-icon" />
        </Button>
        <span>{value?.EXEMPT || 0}</span>
        <Button type="button"
                className="primary outline with-icon"
                disabled={counted(value) === max}
                onClick={() => onValueChange(AgeCategory.EXEMPT, 1)}>
          <IconPlus className="p-icon" />
        </Button>
      </dd>
    </dl>);

};

export default GuestsSelector;
